// Utility functions for filter system integration
import type { FilterOptionsCache } from "./types/filterUtils.types";

// Utility function to convert filters to API parameters
const convertFiltersToApiParams = (filters: Record<string, unknown>): Record<string, string> => {
	const params: Record<string, string> = {};

	Object.entries(filters).forEach(([key, value]) => {
		if (value !== null && value !== undefined && value !== "") {
			if (value instanceof Date) {
				params[key] = value.toISOString().split("T")[0];
			} else if (Array.isArray(value)) {
				params[key] = value.join(",");
			} else {
				params[key] = value.toString();
			}
		}
	});

	return params;
};

// Utility function to build API query string from filters
const buildFilterQueryString = (filters: Record<string, unknown>): string => {
	const params = convertFiltersToApiParams(filters);
	return new URLSearchParams(params).toString();
};

/**
 * Convert filter values to API parameters for different table types
 */
export const createApiParams = (filters: Record<string, unknown>, tableType: string): Record<string, string> => {
	const baseParams = convertFiltersToApiParams(filters);

	// Add table-specific parameter mappings if needed
	switch (tableType) {
		case "institute":
			return {
				...baseParams,
				// Map filter names to API parameter names if different
				// session: baseParams.session,
				// province_id: baseParams.province,
				// division_id: baseParams.division,
				// district_id: baseParams.district,
				// tehsil_id: baseParams.tehsil,
				// union_council_id: baseParams.uc,
				// institute_id: baseParams.institute,
				// stream_id: baseParams.stream,
				// organization_type: baseParams.organizationType,
				// inspection_status: baseParams.inspectionStatus,
				// application_type: baseParams.applicationType,
				// signup_status: baseParams.signupStatus,
				// registration_status: baseParams.registrationStatus,
			};

		case "applications":
		case "scrutiny":
		case "rejected":
		case "scrutinized":
		case "inspected":
			return {
				...baseParams,
				// Map application-specific parameters
				// session: baseParams.session,
				// province_id: baseParams.province,
				// division_id: baseParams.division,
				// district_id: baseParams.district,
				// tehsil_id: baseParams.tehsil,
				// union_council_id: baseParams.uc,
				// institute_id: baseParams.institute,
				// stream_id: baseParams.stream,
				// organization_type: baseParams.organizationType,
				// inspection_status: baseParams.inspectionStatus,
				// application_type: baseParams.applicationType,
				// application_status: baseParams.applicationStatus,
				// from_rejected_date: baseParams.fromRejectedDate,
				// to_rejected_date: baseParams.toRejectedDate,
				// from_resubmitted_date: baseParams.fromResubmittedDate,
				// to_resubmitted_date: baseParams.toResubmittedDate,
				// referred_back: baseParams.referredBack,
			};

		case "committeesHistory":
		case "myCommittees":
		case "committeeRequests":
			return {
				...baseParams,
				// Map committee-specific parameters
				// session: baseParams.session,
				// committee_type: baseParams.committeeType,
			};

		case "unauthorizedInstitutes": {
			const { applicationStatus, ...rest } = baseParams;
			return {
				...rest,
				...(applicationStatus ? { status: applicationStatus } : {}),
			};
		}

		case "members":
			return {
				...baseParams,
				// Map member-specific parameters
				// province_id: baseParams.province,
				// division_id: baseParams.division,
				// district_id: baseParams.district,
				// tehsil_id: baseParams.tehsil,
				// union_council_id: baseParams.uc,
				// member_type: baseParams.memberType,
				// technology_id: baseParams.technology,
				// grade: baseParams.grade,
				// member_org_type: baseParams.memberOrgType,
			};

		default:
			return baseParams;
	}
};

/**
 * Create API query string with filters
 */
export const createApiQueryString = (filters: Record<string, unknown>, tableType: string): string => {
	const params = createApiParams(filters, tableType);
	return buildFilterQueryString(params);
};

/**
 * Merge filters with existing query parameters
 */
export const mergeFiltersWithQuery = (
	existingQuery: Record<string, unknown>,
	filters: Record<string, unknown>,
	tableType: string,
): Record<string, unknown> => {
	const filterParams = createApiParams(filters, tableType);

	return {
		...existingQuery,
		...filterParams,
	};
};

/**
 * Validate filter values before sending to API
 */
export const validateFilters = (filters: Record<string, unknown>): Record<string, unknown> => {
	const validated: Record<string, unknown> = {};

	Object.entries(filters).forEach(([key, value]) => {
		// Skip empty values
		if (value === null || value === undefined || value === "") {
			return;
		}

		// Validate date values
		if (value instanceof Date) {
			if (!isNaN(value.getTime())) {
				validated[key] = value;
			}
			return;
		}

		// Validate string values
		if (typeof value === "string" && value.trim() !== "") {
			validated[key] = value.trim();
			return;
		}

		// Validate number values
		if (typeof value === "number" && !isNaN(value)) {
			validated[key] = value;
			return;
		}

		// Validate array values
		if (Array.isArray(value) && value.length > 0) {
			validated[key] = value;
			return;
		}
	});

	return validated;
};

/**
 * Create pagination parameters
 */
export const createPaginationParams = (page: number, limit: number = 10): Record<string, string> => ({
	page: page.toString(),
	limit: limit.toString(),
});

// Store to cache filter options (streams) for name lookup - only for scrutinized table
let filterOptionsCache: FilterOptionsCache = {};

/**
 * Set filter options cache for name lookups (only for scrutinized table)
 */
export const setFilterOptionsCache = (cache: FilterOptionsCache) => {
	filterOptionsCache = cache;
};

/**
 * Create complete API parameters with filters and pagination
 */
export const createCompleteApiParams = (
	filters: Record<string, unknown>,
	tableType: string,
	page: number = 1,
	limit: number = 10,
): Record<string, string> => {
	const validatedFilters = validateFilters(filters);
	const filterParams = createApiParams(validatedFilters, tableType);
	const paginationParams = createPaginationParams(page, limit);

	const result = {
		...filterParams,
		...paginationParams,
	};

	// Special handling: Convert courseStream ID to name for scrutinized, inspected, and institute table types
	if (result?.courseStream && filterOptionsCache?.courseStream?.length > 0) {
		const stream = filterOptionsCache.courseStream.find(s => s?.value?.toString() === result.courseStream);
		if (stream?.label) {
			result.courseStream = stream.label; // Send name instead of ID
		}
	}

	return result;
};

/**
 * Extract filter values from URL search params
 */
export const extractFiltersFromUrl = (searchParams: URLSearchParams): Record<string, unknown> => {
	const filters: Record<string, unknown> = {};

	searchParams.forEach((value, key) => {
		// Skip pagination parameters
		if (key === "page" || key === "limit") {
			return;
		}

		// Try to parse as number
		const numValue = Number(value);
		if (!isNaN(numValue)) {
			filters[key] = numValue;
			return;
		}

		// Try to parse as date
		const dateValue = new Date(value);
		if (!isNaN(dateValue.getTime())) {
			filters[key] = dateValue;
			return;
		}

		// Default to string
		filters[key] = value;
	});

	return filters;
};

/**
 * Create URL search params from filters
 */
export const createUrlSearchParams = (filters: Record<string, unknown>, tableType: string): URLSearchParams => {
	const params = createApiParams(filters, tableType);
	return new URLSearchParams(params);
};
